import { Sparkles, HelpCircle, PanelRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearch } from './GlobalSearch';
// import { LanguageMenu, ThemeMenu } from './PrefsMenu'; // hidden for now
import { useT } from '@/lib/i18n/useT';
import { FavoriteStar } from '@/features/favorites/FavoriteStar';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useAssistantUi } from '@/features/ai-assistant/assistantUiStore';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { AppSwitcher } from './AppSwitcher';
import { useRightSidebar } from './RightSidebar';
import { useFormPanel } from './formPanelStore';
import { useJourneyPreview } from './journeyPreviewStore';
import { TourLauncher } from '@/features/tour/TourLauncher';

/**
 * Top bar — three-column grid:
 *  · Left   — WorkspaceSwitcher (`Company › Brand` pill + brand picker)
 *  · Center — GlobalSearch (events / brands / stations / stock)
 *  · Right  — help, notifications, favorite, app switcher
 *             (theme/language hidden for now)
 *
 * Left and right columns are `flex-none` and `flex-1`-balanced so the
 * search stays visually centered regardless of the workspace pill width.
 */
export function TopBar() {
  const navigate = useNavigate();
  const t = useT();
  // Atledi lives in its own drawer now, not in the right rail — this used to
  // toggle the rail, which would open an empty work panel instead.
  const toggleAssistant = useAssistantUi((s) => s.toggle);

  // El carril de propiedades: se abre y se pliega desde aquí, junto al
  // selector de aplicaciones, además de con su manija. El botón sólo existe
  // cuando el carril tiene algo que enseñar (un formulario o la previa del
  // Design Journey); sin contenido, el carril ni se monta y el botón sobra.
  const railOpen = useRightSidebar((s) => s.open);
  const toggleRail = useRightSidebar((s) => s.toggle);
  const formActivo = useFormPanel((s) => !!s.active);
  const journeyActivo = useJourneyPreview((s) => s.active);
  const railTieneContenido = formActivo || journeyActivo;

  return (
    // `data-chrome` swaps the content tokens for the chrome palette across the
    // whole subtree, so the shared widgets below (workspace switcher, search,
    // bell, …) render dark here without knowing about a second token set.
    <header
      data-chrome
      className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 sm:gap-4 sm:px-6 md:px-8"
    >
      {/* Left — workspace context. */}
      <div className="hidden flex-1 lg:flex" data-tour="topbar-workspace">
        <WorkspaceSwitcher />
      </div>

      {/* Center — global search, kept visually centered by the flex-1
          flankers on each side. */}
      <div data-tour="topbar-search" className="min-w-0 flex-1 md:max-w-xl">
        <GlobalSearch className="w-full" />
      </div>

      {/* Right-side controls — aligned to the far right. */}
      <div className="flex flex-1 items-center justify-end gap-1.5 sm:gap-2 md:gap-3">
        {/* Theme + language switchers — temporarily hidden per design feedback.
            Re-enable by uncommenting and restoring the import above. */}
        {/* <div className="hidden items-center gap-2 md:flex md:gap-4">
          <ThemeMenu />
          <LanguageMenu />
        </div> */}

        {/* AI assistant — mobile/tablet only. On lg+ the docked right
            sidebar provides its own toggle, so this is hidden there. */}
        <button
          type="button"
          onClick={toggleAssistant}
          aria-label={t('assistant.expand')}
          className="grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-accent-ink)] transition-colors hover:bg-[var(--color-xk-surface)] lg:hidden"
        >
          <Sparkles className="h-5 w-5" />
        </button>

        {/* Product tour — replays the current page's walkthrough. Self-hides
            on pages without a tour. */}
        <TourLauncher />

        {/* Help — always visible. Routes to the in-app Help Center (/help). */}
        <button
          type="button"
          data-tour="topbar-help"
          onClick={() => navigate('/help')}
          aria-label={t('topbar.help')}
          title={t('topbar.help')}
          className="grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-accent-ink)]"
        >
          <HelpCircle className="h-5 w-5" />
        </button>

        {/* Notifications bell — always visible. */}
        <NotificationBell />

        {/* Favorite the current page — hidden on small phones. */}
        <div className="hidden sm:block">
          <FavoriteStar />
        </div>

        {/* App switcher — the outermost level of navigation there is: which
            product you're in. It sits at the far right, where the launcher
            lives in every suite people already use, and outside everything
            else because the rest of this bar is context *within* the app it
            chooses. It stays visible on mobile, unlike the workspace pill:
            on a phone it's the only way across to another app. */}
        {railTieneContenido ? (
          <button
            type="button"
            data-tour="topbar-rail"
            onClick={toggleRail}
            aria-pressed={railOpen}
            aria-label={railOpen ? t('rightRail.collapse') : t('rightRail.expand')}
            title={railOpen ? t('rightRail.collapse') : t('rightRail.expand')}
            className={
              railOpen
                ? 'grid h-10 w-10 flex-none place-items-center rounded-md bg-[var(--color-xk-surface)] text-[var(--color-accent-ink)] transition-colors'
                : 'grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-accent-ink)]'
            }
          >
            <PanelRight className="h-5 w-5" />
          </button>
        ) : null}
        <AppSwitcher />
      </div>
    </header>
  );
}
